// 15 diagnostic questions, 3 per category
// category keys match flexxState.graph keys

export const DIAGNOSTIC_QUESTIONS = [
  // --- Market Structure ---
  {
    id: "ms1",
    category: "marketStructure",
    q: "What does a bearish engulfing candlestick pattern typically signal?",
    choices: [
      "Continuation of an uptrend",
      "Potential reversal from uptrend to downtrend",
      "Market indecision with no bias",
      "Strong buying pressure coming in",
    ],
    correct: 1,
  },
  {
    id: "ms2",
    category: "marketStructure",
    q: "Support is best described as…",
    choices: [
      "A price level where price tends to reverse downward",
      "A price level where buying interest historically steps in",
      "A level where volume always spikes to a maximum",
      "A price zone that never breaks",
    ],
    correct: 1,
  },
  {
    id: "ms3",
    category: "marketStructure",
    q: "A market is in a confirmed uptrend when it is printing…",
    choices: [
      "Lower highs and lower lows",
      "Higher highs and higher lows",
      "Equal highs and equal lows",
      "Only doji candles",
    ],
    correct: 1,
  },

  // --- Technical Analysis ---
  {
    id: "ta1",
    category: "ta",
    q: "Which indicator is primarily used to measure momentum and overbought/oversold conditions?",
    choices: ["Moving Average (MA)", "Bollinger Bands", "RSI", "Volume Profile"],
    correct: 2,
  },
  {
    id: "ta2",
    category: "ta",
    q: "What does a 'golden cross' generally indicate?",
    choices: [
      "A long-term bearish reversal signal",
      "The 50 MA crossing above the 200 MA — bullish bias",
      "A divergence between RSI and price",
      "A short-term exhaustion of a rally",
    ],
    correct: 1,
  },
  {
    id: "ta3",
    category: "ta",
    q: "Bullish divergence occurs when…",
    choices: [
      "Price makes a lower low but indicator makes a higher low",
      "Price and indicator both make new highs",
      "Price is flat while indicator spikes",
      "RSI stays above 70 for several candles",
    ],
    correct: 0,
  },

  // --- Risk ---
  {
    id: "r1",
    category: "risk",
    q: "If you risk 2% of a $5,000 account per trade, how much are you risking in dollars?",
    choices: ["$50", "$100", "$200", "$500"],
    correct: 2,
  },
  {
    id: "r2",
    category: "risk",
    q: "A Risk:Reward ratio of 1:3 means…",
    choices: [
      "You risk $3 to make $1",
      "You risk $1 to make $3",
      "You win 3 times out of every trade",
      "Your stop loss is 3× your take profit",
    ],
    correct: 1,
  },
  {
    id: "r3",
    category: "risk",
    q: "What is drawdown?",
    choices: [
      "The peak-to-trough decline of an account before a new peak is made",
      "The total profit made in a quarter",
      "The number of losing trades this month",
      "The spread charged by a broker",
    ],
    correct: 0,
  },

  // --- Psychology ---
  {
    id: "p1",
    category: "psychology",
    q: "When you take a loss, what is the healthiest first reaction?",
    choices: [
      "Immediately re-enter to win back the loss",
      "Increase your position size on the next trade",
      "Review whether the trade followed your plan, then move on",
      "Stop trading for a week regardless of the setup",
    ],
    correct: 2,
  },
  {
    id: "p2",
    category: "psychology",
    q: "FOMO in trading refers to…",
    choices: [
      "A strict position-sizing formula",
      "Fear of missing out — entering late due to emotion, not logic",
      "A candlestick pattern used at tops",
      "A risk management indicator",
    ],
    correct: 1,
  },
  {
    id: "p3",
    category: "psychology",
    q: "'Revenge trading' is best defined as…",
    choices: [
      "Placing impulsive trades to recover recent losses",
      "A disciplined approach to hedging",
      "Scaling out of a winning position",
      "A planned averaging-down strategy",
    ],
    correct: 0,
  },

  // --- Strategy ---
  {
    id: "s1",
    category: "strategy",
    q: "A tradable edge is best described as…",
    choices: [
      "A rule-based system with a positive expectancy over a large sample",
      "A single winning trade that paid off well",
      "Any feeling that a market will go up",
      "A tip from a paid signal group",
    ],
    correct: 0,
  },
  {
    id: "s2",
    category: "strategy",
    q: "Expectancy is calculated as…",
    choices: [
      "Win Rate × Account Balance",
      "(Win Rate × Avg Win) − (Loss Rate × Avg Loss)",
      "Number of trades × R:R",
      "The sum of all wins divided by 100",
    ],
    correct: 1,
  },
  {
    id: "s3",
    category: "strategy",
    q: "What is the role of backtesting?",
    choices: [
      "Predicting the future exactly",
      "Eliminating all losing trades",
      "Measuring how a rule-based strategy performed historically to estimate its edge",
      "Guaranteeing profitability going forward",
    ],
    correct: 2,
  },
];

// Score diagnostic: returns { total: 0-100, perCategory: {...}, track, idkCount }
//
// Each question has 4 knowledge options (A-D) plus "I don't know" (E, index 4).
// IDK answers are counted as INCORRECT (0 points) — same as a wrong answer.
// This eliminates the ~25% lucky-guess inflation that previously placed
// beginners into intermediate/advanced tracks. We also surface idkCount in
// the result so the post-quiz screen can acknowledge honest self-assessment.
//
// Track placement override: if the student admits "I don't know" on more
// than half the questions (>= 8 of 15), force foundational track regardless
// of accidental correct guesses on the rest.
export const IDK_INDEX = 4;
export const IDK_LABEL = "I don't know — teach me from the start";

export function scoreDiagnostic(answers) {
  const cats = ["marketStructure", "ta", "risk", "psychology", "strategy"];
  const perCategory = Object.fromEntries(cats.map((c) => [c, { correct: 0, total: 0, idk: 0 }]));
  let idkCount = 0;
  DIAGNOSTIC_QUESTIONS.forEach((q) => {
    perCategory[q.category].total += 1;
    const ans = answers[q.id];
    if (ans === IDK_INDEX) {
      idkCount += 1;
      perCategory[q.category].idk += 1;
    } else if (ans === q.correct) {
      perCategory[q.category].correct += 1;
    }
  });
  const pct = {};
  cats.forEach((c) => {
    const { correct, total } = perCategory[c];
    pct[c] = total ? Math.round((correct / total) * 100) : 0;
  });
  const total = Math.round(
    (DIAGNOSTIC_QUESTIONS.reduce((acc, q) => acc + (answers[q.id] === q.correct ? 1 : 0), 0) /
      DIAGNOSTIC_QUESTIONS.length) *
      100
  );
  let track = "advanced";
  if (total <= 40) track = "foundational";
  else if (total <= 75) track = "intermediate";
  // Honesty-override: if half-or-more questions are IDK, force foundational
  // even if the rest happen to be lucky-correct. Better placement > vanity score.
  if (idkCount >= 8) track = "foundational";
  return { total, perCategory: pct, track, idkCount };
}

export const TRACK_LABEL = {
  foundational: { name: "Foundational Track", blurb: "Full beginner path — build rock-solid fundamentals first.", color: "#6daa45" },
  intermediate: { name: "Intermediate Track", blurb: "Skip the basics, jump straight into Technical Analysis and execution.", color: "#C9A227" },
  advanced: { name: "Advanced Track", blurb: "Strategy design, backtesting and live execution focus.", color: "#01696f" },
};
